// src/components/auth/CantReachScreen.jsx
// Shown instead of the login page when someone IS signed in, but their own account
// record could not be loaded because the server could not be reached: weak or no
// signal at a showing, in a basement, at an open house (2026-09-14, speed wave 4).
// The login page was misleading there, since signing in again needs signal too.
// It is also what a launch shows when Firebase's sign-in never answers at all, on a
// device somebody has signed in on before (App.js, 09/16/2026 review).
//
// App.js owns the retrying: this screen's button tries at once, and App.js also tries
// again by itself when the connection comes back or the app is brought back to the
// screen. A missing record never lands here; it still goes to the login page.
//
// 09/16/2026 review, two things this screen was missing:
//   - a way out. App.js returns this screen INSTEAD of the route table, so while it is
//     up the login page and every other route are unreachable. On a shared iPad that
//     left nobody able to get out of someone else's signed-in account. Sign Out sits
//     under Retry, goes through App's sign-out for this screen, and lands on the login
//     page. It stays available while a retry is running, since that retry may be waiting
//     on the very signal that is missing. While a sign-out is running it reads
//     "Signing Out..." and takes no second tap (second review round, same day).
//   - any word about a failed try. A Retry that failed left the screen looking exactly
//     as it had a moment earlier, so it could not be told from a tap that had not
//     registered. A failed attempt now says so, and names the time it last tried.
//
// Third review round, same day: when Firebase's sign-in never answered at all (reason
// "no-answer"), the app knows neither that a connection dropped nor that anyone is
// still signed in, so the screen no longer says either. It says what is known.
//
// Fourth review round, same day: nor does it know why sign-in has not answered. On an
// iPhone the usual cause is the phone's own storage hanging, not the server, so that
// wording names no cause. On that path App.js makes Retry reload the app while the
// browser has a signal, since a fresh start is the only retry there is.
//
// Fifth review round, same day: the line about a failed try named the server too, on
// that same screen. There it now says only that the sign-in still could not be checked.

import { chicagoParts } from "../../utils/chicagoTime";

// "Last tried at 2:14pm". The same clock format the rest of the app writes
// (formatTime12 in utils/timeline.js is the one formatter, and its test file pins these
// two to the same answers). The rule is mirrored here rather than imported because this
// screen is part of the file every launch downloads, and importing the timeline module
// for one line of text would pull its catalog and its open-house helpers in with it.
// Read on the Minnesota clock, whatever zone the device is set to.
function clockTime(when) {
  const p = chicagoParts(when);
  if (!p) return "";
  const hours = p.hour;
  const minutes = String(p.minute).padStart(2, "0");
  return `${((hours + 11) % 12) + 1}:${minutes}${hours >= 12 ? "pm" : "am"}`;
}

export default function CantReachScreen({
  retrying,
  onRetry,
  onSignOut,
  lastFailedAt = null,
  signingOut = false,
  reason = "record",
}) {
  const noAnswer = reason === "no-answer";
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-sm p-6 text-center">
        <h1 className="text-xl font-bold text-[#1E293B]">Can't Reach Team Edelstein</h1>
        <p className="mt-2 text-sm text-slate-600">
          {noAnswer
            ? "Your sign-in could not be checked yet."
            : "Your account could not be loaded because the connection to the server dropped. You are still signed in."}
        </p>
        <p className="mt-2 text-sm text-slate-500">
          {noAnswer
            ? "This screen moves on by itself as soon as the check finishes."
            : "This screen tries again on its own when your signal comes back."}
        </p>
        {/* Retry also waits while a sign-out is running: letting the person back in while
            they are leaving makes no sense. */}
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying || signingOut}
          className="mt-5 w-full rounded-lg bg-[#1E293B] text-white text-sm font-semibold py-2.5 disabled:opacity-60"
        >
          {retrying ? "Trying Again..." : "Retry"}
        </button>
        {/* The way out: signing out is the only path back to the login page from here, and
            the only way to hand a shared iPad to somebody else. Never disabled by a retry.
            Disabled only while a sign-out is already running (09/16/2026, second review
            round): that sign-out can wait behind Firebase's unfinished start-up, and a button
            that showed nothing meanwhile got tapped again and again. */}
        <button
          type="button"
          onClick={onSignOut}
          disabled={signingOut}
          className="mt-2 w-full rounded-lg border border-slate-300 bg-white text-[#1E293B] text-sm font-semibold py-2.5 disabled:opacity-60"
        >
          {signingOut ? "Signing Out..." : "Sign Out"}
        </button>
        {lastFailedAt ? (
          <p className="mt-3 text-sm text-slate-500" role="status">
            {noAnswer
              ? `Still could not check your sign-in. Last tried at ${clockTime(lastFailedAt)}.`
              : `Still cannot reach the server. Last tried at ${clockTime(lastFailedAt)}.`}
          </p>
        ) : null}
      </div>
    </div>
  );
}
